import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CreditCard, Lock, Check, LogOut } from "lucide-react";
import api from "../services/api";
import { logout } from "../redux/authSlice";
import { PageLoader } from '../components/crm/ui';

const FEATURES = [
    "Send bulk WhatsApp messages",
    "Connect WhatsApp devices",
    "Auto-reply automation",
    "Full CRM — leads, deals, contacts & accounts",
];

/**
 * SubscriptionGate
 * Wraps the dashboard content. If the tenant's subscription is expired or inactive,
 * shows a full-screen paywall instead of the page content.
 *
 * The subscription and invoices pages themselves are always accessible so the
 * tenant can subscribe or review what they've already paid for.
 */
function SubscriptionGate({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [subStatus, setSubStatus] = useState("loading"); // loading | active | inactive

    const isSubPage =
        location.pathname.startsWith("/dashboard/subscription") ||
        location.pathname.startsWith("/dashboard/invoices");

    useEffect(() => {
        if (isSubPage) {
            setSubStatus("active"); // skip check for subscription-related pages
            return;
        }
        checkSubscription();
    }, [location.pathname]);

    const checkSubscription = async () => {
        try {
            const { data } = await api.get("/subscriptions/current");
            const active = data.subscription?.isActive;
            setSubStatus(active ? "active" : "inactive");
        } catch (error) {
            if (error.response?.status === 401) {
                dispatch(logout());
                navigate("/login", { replace: true });
                return;
            }
            // If the check itself fails (e.g. network error), don't block the user.
            setSubStatus("active");
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate("/login", { replace: true });
    };

    if (subStatus === "loading") {
        return (
            <PageLoader />
        );
    }

    if (subStatus === "inactive") {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F6F9] px-4 py-12">
                <div className="w-full max-w-[440px] bg-white border border-[#D8DDE6] rounded-[8px] shadow-sm p-8 text-center">
                    <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-[rgba(1,118,211,0.08)] flex items-center justify-center">
                        <Lock size={26} className="text-[#0176D3]" />
                    </div>

                    <h2 className="text-[20px] font-bold text-[#181818]">Subscription Required</h2>
                    <p className="mt-2 text-[13px] text-[#706E6B] leading-relaxed">
                        Your subscription has expired or isn't active yet. Choose a plan to unlock
                        full access to your workspace.
                    </p>

                    <ul className="mt-6 space-y-2.5 text-left">
                        {FEATURES.map((feature) => (
                            <li key={feature} className="flex items-start gap-2.5 text-[13px] text-[#181818]">
                                <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-[rgba(4,132,75,0.1)] flex items-center justify-center">
                                    <Check size={11} className="text-[#04844B]" strokeWidth={3} />
                                </span>
                                {feature}
                            </li>
                        ))}
                    </ul>

                    <button
                        onClick={() => navigate("/dashboard/subscription")}
                        className="mt-7 w-full h-11 rounded-[4px] bg-[#0176D3] hover:bg-[#005FB2] text-white text-[14px] font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        <CreditCard size={17} /> View Plans &amp; Subscribe
                    </button>

                    <button
                        onClick={handleLogout}
                        className="mt-3 w-full h-9 rounded-[4px] text-[#706E6B] hover:text-[#181818] hover:bg-[#F3F2F2] text-[12px] font-bold flex items-center justify-center gap-1.5 transition-colors"
                    >
                        <LogOut size={13} /> Sign out
                    </button>
                </div>
            </div>
        );
    }

    return children;
}

export default SubscriptionGate;
